import { useCallback, useMemo, useState } from 'react';
import { sortRowsByKey } from '../services/player';

export type SortDirection = 'asc' | 'desc';

interface UseTableSortOptions<T, K extends keyof T | string> {
  rows: T[] | null | undefined;
  initialSortKey?: K | null;
  initialDirection?: SortDirection;
  nextKeyDirection?: SortDirection;
  sortRows?: (rows: T[], key: K, direction: SortDirection) => T[];
}

export function useTableSort<T, K extends keyof T | string>(
  options: UseTableSortOptions<T, K>,
) {
  const {
    rows,
    initialSortKey = null,
    initialDirection = 'desc',
    nextKeyDirection = 'desc',
    sortRows,
  } = options;

  const [sortKey, setSortKey] = useState<K | null>(initialSortKey);
  const [sortDirection, setSortDirection] = useState<SortDirection>(initialDirection);

  const handleSort = useCallback((key: K) => {
    if (sortKey === key) {
      setSortDirection((previous) => (previous === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(key);
    setSortDirection(nextKeyDirection);
  }, [sortKey, nextKeyDirection]);

  const sortedRows = useMemo(() => {
    const list = [...(rows || [])];
    if (!sortKey) {
      return list;
    }

    if (sortRows) {
      return sortRows(list, sortKey, sortDirection);
    }

    return sortRowsByKey(list as Record<string, any>[], sortKey as string, sortDirection) as T[];
  }, [rows, sortKey, sortDirection, sortRows]);

  return {
    sortKey,
    sortDirection,
    handleSort,
    sortedRows,
    setSortKey,
    setSortDirection,
  };
}
